import { ElementRef } from '@angular/core';

interface DateInfo {
  day: string;
  month: string;
  year: number;
}

export class DateUtil {
  static formatInputDate(value: string): string {
    const date = new Date(value);
    const [month, day, year] = [date.getMonth(), date.getDate(), date.getFullYear()];
    const _month = `${month + 1}`.padStart(2, '0');
    const _day = `${day}`.padStart(2, '0');
    return `${_day}.${_month}.${year}`;
  }

  static setDateTimeInputValue(inputRef: ElementRef, value: string) {
    const input = inputRef.nativeElement;
    input.value = DateUtil.formatInputDate(value);
    input.dispatchEvent(new Event('input'));
  }

  static formatDate(date: Date): string {
    const info = this.parseDateInfo(date);
    return `${info.day}.${info.month}.${info.year}`;
  }

  static formatDateToAPI(date: Date): string {
    //2023-12-31T22:00:00.000Z
    const info = this.parseDateInfo(date);
    return `${info.year}-${info.month}-${info.day}`;
  }

  static getFirstDateOfYear(year: number = new Date().getFullYear()): Date {
    return new Date(year, 0, 1);
  }

  static getLastDateOfYear(year: number = new Date().getFullYear()): Date {
    return new Date(year, 11, 31);
  }

  static formatDateRange(dateFrom?: string, dateTo?: string): Record<string, string> {
    let dateFromRange = dateFrom ? dateFrom : DateUtil.formatDateToAPI(DateUtil.getFirstDateOfYear());
    let dateToRange = dateTo ? dateTo : DateUtil.formatDateToAPI(DateUtil.getLastDateOfYear());

    if (dateFrom != null && dateTo === null) {
      const dateFrom = new Date(dateFromRange);
      const dateTo = new Date(dateFrom.getFullYear() + 2, 11, 31);
      dateToRange = DateUtil.formatDateToAPI(dateTo);
    }

    if (dateFrom === null && dateTo != null) {
      const dateTo = new Date(dateToRange);
      const dateFrom = new Date(dateTo.getFullYear() - 2, 0, 1);
      dateFromRange = DateUtil.formatDateToAPI(dateFrom);
    }
    return { DateFrom: dateFromRange, DateTo: dateToRange };
  }

  static getMinYear(...dates: (string | null | undefined)[]): number | null {
    const validYears: number[] = [];

    for (const dateStr of dates) {
      if (dateStr) {
        // Check for non-null or non-empty string
        const dateParts = dateStr.split('-');
        if (dateParts.length === 3) {
          const year = parseInt(dateParts[0], 10); // Extract the year (first part)
          if (!isNaN(year)) {
            validYears.push(year);
          }
        }
      }
    }

    if (validYears.length > 0) {
      return Math.min(...validYears);
    } else {
      return new Date().getFullYear(); // No valid dates provided
    }
  }

  static daysToEndDate(endDate: string | Date): number {
    const now = new Date();
    const target = new Date(endDate);

    // Clear time part for accurate difference in days
    now.setHours(0, 0, 0, 0);
    target.setHours(0, 0, 0, 0);

    const diffTime = target.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays;
  }

  private static parseDateInfo(date: Date): DateInfo {
    const [month, day, year] = [date.getMonth(), date.getDate(), date.getFullYear()];
    const _month = `${month + 1}`.padStart(2, '0');
    const _day = `${day}`.padStart(2, '0');
    return {
      day: _day,
      month: _month,
      year: year,
    };
  }
}

export abstract class DateYears {
  private readonly _currentYear = new Date().getFullYear();
  readonly minDate = new Date(2020, 0, 1);
  readonly maxDate = new Date(this._currentYear, 11, 31);
}
